<template>
  <div  class="columns is-centered" v-if="chartOptions.option && isLoaded">
    <div class="column is-12-tablet is-11-desktop">
      <vue-highcharts
        type="chart"
        :options="chartOptions.option"
        :redrawOnUpdate="true"
        :oneToOneUpdate="true"
        :animateOnUpdate="true"
        @rendered="onRender"
        @update="onUpdate"
        @destroy="onDestroy"/>
    </div>
  </div>
</template>

<script>
/*

*/
import { reactive, ref, watch } from 'vue';
import { useApp } from '@/composables/state/profileState';
import Vue3Highcharts from '@/components/vue3-highcharts/index';
import { osrHFTabs, credits, defaultXAxis, defaultYAxis, yTickLabelFormatter0 } from '@/config/profile-config';
import { mergeDeep } from '@/lib/common';

export default {
  name: 'osr-single-sector-effect-bar-chart',
  components: {
    'vue-highcharts': Vue3Highcharts
  },
  props: ['dataType'],
  setup (props) {
    const { osrChartData } = useApp();
    const series = ref();
    const chartOptions = reactive({});
    const isLoaded = ref(false);
    const packageData = () => {
      if (!osrChartData || osrChartData.length === 0) {
        return;
      }
      const yRange = [-100, 100]; // props.dataType === 'total' ? [-2, 2] : [-100, 100];
      const dotDistanceFromBar = 5; // props.dataType === 'total' ? 0.2 : 5;
      Object.assign(chartOptions, {});
      const categories = ref();
      categories.value = osrHFTabs.map(x => x.label);
      const mean = [];
      const topError = [];
      const bottomError = [];
      const dotSeries = [];
      const colors = [];

      osrHFTabs.forEach(hfType => {
        /* add category with two levels */
        const row = osrChartData.find(x => {
          return x.label === hfType.label;
        });
        if (row != null) {
          const sortData = row[props.dataType];

          const orgY = sortData[1];
          const midY = orgY > yRange[1] ? yRange[1] : (orgY < yRange[0] ? yRange[0] : orgY)
          const upperY = sortData[2] > yRange[1] ? yRange[1] : sortData[2];
          const lowerY = sortData[0] < yRange[0] ? yRange[0] : sortData[0];
          mean.push({
            dataLabels: {
              verticalAlign: (orgY > 0 ? 'bottom' : 'top'),
              x: 0,
              y: (orgY > 0 ? 1 : -1) * 25
            },
            y: midY,
            orgY
          });
          topError.push(sortData[0] > 0 ? [midY, upperY] : [lowerY, midY]); // outside main bar, using same colors as the bar
          bottomError.push(sortData[0] > 0 ? [lowerY, midY] : [midY, upperY]); // overlap with main bar so using white color
          colors.push(row.color);

          dotSeries.push({
            orgY,
            y: orgY > yRange[1] ? yRange[1] + dotDistanceFromBar : (orgY < yRange[0] ? yRange[0] - dotDistanceFromBar : null) // optioal dots
          });
        } else {
          mean.push(null);
          topError.push(null); // above main bar, using same colors as the bar
          bottomError.push(null); // overlap with main bar so using white color
          colors.push(null);
          dotSeries.push(null);
        }
      });

      series.value = [
        {
          // type: 'column',
          data: mean
        },
        {
          type: 'errorbar',
          whiskerWidth: 0,
          data: topError,
          colorByPoint: true,
          colors: colors
        },
        {
          type: 'errorbar',
          whiskerWidth: 0,
          data: bottomError,
          color: '#ffffff'
        },
        {
          type: 'line',
          data: dotSeries,
          colors: colors
        }
      ];

      chartOptions.option = {
        credits,
        chart: {
          type: 'column',
          marginTop: 30
        },
        title: {
          text: '' // chart[type].title
        },
        xAxis: mergeDeep(defaultXAxis,
          {
            categories: categories,
            labels: { /* make labels display in multiple lines to avoid overflow */
              formatter: function () {
                const label = this.axis.defaultLabelFormatter.call(this);
                return label.replace(' ', '<br>');
              },
              useHTML: true
            }
          }),
        yAxis: mergeDeep(defaultYAxis,
          {
            min: yRange[0] - dotDistanceFromBar * 2,
            max: yRange[1] + dotDistanceFromBar * 2,
            title: {
              text: props.dataType === 'under' ? 'Under-footprint Sector Effects (%)' : 'Regional Sector Effects (%)'
            },
            labels: yTickLabelFormatter0,
            endOnTick: false,
            startOnTick: false,
          }),
        plotOptions: {
          series: {
            colorByPoint: true,
          },
          column: {
            stacking: 'normal', // IMPORTANT: required this to make verticalAlign to work (datalabels)
            colorByPoint: true,
            colors: colors,
            groupPadding: 0.2,
            dataLabels: {
              // verticalAlign: 'bottom',
              enabled: true,
              style: {
                color: '#333333'
              },
              formatter: function () {
                return (this.point.y > 0 ? '+' : '') + this.point.orgY.toFixed(2) + '%'; // + useHFCustomIcon(extenedHfCategory, 15, this.point.name)
              }
            }
          },
          line: {
            lineWidth: 0,
            dataLabels: {
              enabled: false
            }
          },
        },
        legend: {
          enabled: false
        },
        tooltip: {
          backgroundColor: '#FCFFC5',
          headerFormat: '',
          pointFormatter: function () {
            if (this.options.orgY) {
              return this.category + ': ' + this.options.orgY.toFixed(2) + '%';
            }
            return '';
          }
          // pointFormat: `${point.name}: {point.y}%`
        },
        series: series.value
      }
      isLoaded.value = true
    };

    packageData();
    watch(() => osrChartData?.length, async (newVal, oldVal) => {
      if (newVal && newVal !== oldVal) {
        packageData();
      }
    });
    return {
      chartOptions,
      isLoaded
    }
  }
}
</script>
